import moment from 'moment-timezone'
import React, {useEffect, useRef, useState} from 'react'
import {Customer} from '../components/MakeReservation/MakeReservation'
import {paymentMethod} from '../components/MakeReservation/PaymentInfo'
import {ICoupon, IVehicleSearch} from '../interfaces/VehicleSearch'
import {User} from '../interfaces/User'
import {AUTO_CUSTOMER_INFO_KEY, ga4Event, ga4SetUserId, ga4SetUserProperties, GA4Params, sendVirtualPageView, virtualPageViews} from "../services/GoogleAnalyticsEvents";
import {Check24rCookie, getCookie, GetSurface, ICustomerBehaviourData} from "../services/CustomerBehaviourData";
import {FilterParameters, IAcrissCodes} from "../components/VehicleSearch/Filter";
import {handleUrlWithHashtag, getBrowserName} from "../GetUrlParams";
import {validateStartEndValues} from "../components/CheckSearchTimesValid";
import {buildPurchasePayload, firePurchaseEvent, storePendingPurchase, clearPendingPurchase} from "../services/PurchaseGtagEvent";
import {clearWalleyPaymentToken} from "../services/walleyPaymentPoller";
import {paymentTypeLabel} from "../services/ga4Ecommerce";
import {expressedSegmentFromSearchParams, segmentFromSearchParams, VehicleSegment} from "../services/vehicleSegment";
import {cacheMapData} from "../services/mapDataCache";
import { normalizeCouponCode } from "../services/couponCode";
import { urlTriggersStartupSearch } from '../services/startupSearch';

export const appContextAndRootURL = (pageUrl = window.location.host) => {
  // this function controls the API version, base assets path and app context for entire app
  let APP_CONTEXT = 'staging';
  let ROOT_URL = '';
  let v3_API_URL = '';
  let walley_API_URL = '';
  switch (pageUrl) {
    case "dev-rent.24rent.fi":
      APP_CONTEXT = "staging";
      v3_API_URL = import.meta.env.VITE_API_V3_STAGING_URL ?? '';
      walley_API_URL = import.meta.env.VITE_API_WALLEY_STAGING_URL ?? '';
      return { APP_CONTEXT, ROOT_URL, v3_API_URL, walley_API_URL}
    case "rent.24rent.fi":
      APP_CONTEXT = "production";
      v3_API_URL = import.meta.env.VITE_API_V3_PROD_URL ?? '';
      walley_API_URL = import.meta.env.VITE_API_WALLEY_PROD_URL ?? '';
      return { APP_CONTEXT, ROOT_URL, v3_API_URL, walley_API_URL}
    case "dev-frontend.24rent.fi":
      APP_CONTEXT = "development";
      ROOT_URL = "/wp-content/plugins/24rent-reservation-machine/build";
      v3_API_URL = import.meta.env.VITE_API_V3_STAGING_URL ?? '';
      walley_API_URL = import.meta.env.VITE_API_WALLEY_STAGING_URL ?? '';
      return { APP_CONTEXT, ROOT_URL, v3_API_URL, walley_API_URL}
    case "www.24rent.fi":
      APP_CONTEXT = "production";
      ROOT_URL = "/wp-content/plugins/24rent-reservation-machine/build";
      v3_API_URL = import.meta.env.VITE_API_V3_PROD_URL ?? '';
      walley_API_URL = import.meta.env.VITE_API_WALLEY_PROD_URL ?? '';
      return { APP_CONTEXT, ROOT_URL, v3_API_URL, walley_API_URL}

    default:
      console.log("Running localhost --> all default params")
      v3_API_URL = import.meta.env.VITE_API_V3_STAGING_URL ?? '';
      walley_API_URL = import.meta.env.VITE_API_WALLEY_STAGING_URL ?? '';
      return { APP_CONTEXT, ROOT_URL, v3_API_URL, walley_API_URL} // localhost
  }
}

const {APP_CONTEXT, ROOT_URL, v3_API_URL, walley_API_URL} = appContextAndRootURL();

const getLocalObject = (storedKey: string) => {
  const p = sessionStorage.getItem(storedKey);
  // UGH: someone decided it was a good idea to write a string 'undefined' to this session variable
  if (p && p !== 'undefined') {
    return JSON.parse(p)
  }
  return undefined;
}

const checkDeviceType = () => {
  // check if desktop or mobile device: 2 checks
  // if mobile, BE will limit number of results returned to FE
  let check = false;
  (function (a) {
    if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od|ad)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw-(n|u)|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do(c|p)o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c(-|0|1)|47|mc|nd|ri)|sgh-|shar|sie(-|m)|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel(i|m)|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i.test(a.substr(0, 4))) {
      check = true;
    }
  }(navigator.userAgent || navigator.vendor || (window as any).opera));

  if (window.innerWidth < 768 || window.screen.width < 768) { // min desktop width = 768
    check = true;
  }
  return check;
}

export interface IMarker {
  vehicleId: number;
  marker: google.maps.marker.AdvancedMarkerElement;
  inCluster: boolean;
}

export interface ISearchParameters {
  location: google.maps.places.PlaceResult | any | null,
  pickupCity?: string;
  returnCity?: string;
  startDate: moment.Moment | null,
  startTime: string,
  endDate: moment.Moment | null,
  endTime: string,
  // set alongside start/end when a search is submitted; read by the summary bar
  pickupDate?: moment.Moment | null,
  returnDate?: moment.Moment | null,
  lat: number,
  lon: number,
  distance?: number,
  km?: number,
  vehicleType?: string,
  // Binary customer segment (car | van) for GA4 funnel analysis. Set explicitly by the
  // search buttons / results toggle (later phase); falls back to `vehicleType` filter.
  vehicleSegment?: VehicleSegment,
  minTrunkVolume?: number,
  fuelType?: string,
  crossBorderArea?: string,
  transmission?: string,
  features?: string,
  sort?: string,
  showStart?: number,
  showAmount?: number,
  carId?: number,
  discount?: string,
  other?: string,
  utm_campaign?: string,
  utm_source?: string,
  utm_term?: string,
  utm_medium?: string,
  utm_content?: string,
  gclid?: string,
  cid?: string,
  cookie?: string | null,
  mobileDevice?: boolean,
  userPlaceSearch?: string;
  searchCity?: string; // Google Maps locality of the searched place — backend maps it to a city id
  pointsInEurosToBeApplied?: number;
}

const serviceDefault = {
  kmRange: 100,
  insurance: '1030,',
  export: false,
  exportDestination: '' as any,
  paymentMethod: paymentMethod.PAYTRAIL,
  handTruck: true,
  unlimitedKilometers: '',
  additionalDriver: false,
  addDriverNumber: '',
  addDriverName: '',
  hook: false,
  windshield: false,
  tireInsurance: false,
  cancellationInsurance: false,
  costPool: '',
  co2compensation: '',
  safeDrivePackage: false
  // one-way service indicated used directly from state below
}

const isDiscountCodeVip = (discountCode: string | undefined) => {
  // do not send url vip code in res create object if non-vip vehicle selected
  const search = handleUrlWithHashtag();
  let urlVipCode = new URLSearchParams(search).get("vip") ?? undefined;
  if (!urlVipCode) return discountCode;

  if (urlVipCode === discountCode) {
    return undefined;
  }

  return discountCode;
}

export type RedirectRef = "chauffeur" | "launcher";

export interface IData {
  rootUrl?: string,
  appContext?: string,
  v3ApiUrl?: string;
  walleyApiUrl?: string;
  redirectRef?: RedirectRef;
  mapMarkers: IMarker[],
  filteredParams: FilterParameters,
  filteredVehicles?: IVehicleSearch[],
  noUnderTwentyOne: boolean,
  mapEnabled?: boolean,
  customer?: Customer,
  searchParameters?: ISearchParameters,
  user?: User,
  vehicle?: IVehicleSearch,
  mapData?: IVehicleSearch[],
  services?: any,
  reservation?: any,
  language?: string,
  route?: string,
  page?: number,
  twentyModal?: boolean,
  outOfRange?: boolean,
  stars?: [],
  mobileDevice?: boolean,
  reservationCreated?: any,
  acrissCodeFromUrl?: string,
  modelId?: number[],
  mapCanInit?: boolean;
  authHashLogin?: boolean;
  acrissCodeSet?: IAcrissCodes | undefined;
  showOneWayReturnLocation?: number | undefined;
  openPopupInCluster?: number | undefined;
  useCompanyInfo?: boolean;
  resCancelled?: boolean;
  changeSearchParameters: Function,
  changeUser: Function,
  changeUserLanguage: (language: string) => void,
  changeVehicle: Function,
  changeMapData: Function,
  changeShowDefaultResults: Function,
  showDefaultResults?: boolean,
  changeMapEnable: Function,
  changeCustomer: Function,
  changeServices: Function,
  changeFilteredList: Function,
  changeFilterParams: Function,
  changeLoggedIn: Function,
  changeRoute: Function,
  changePage: Function,
  changeLanguage: Function,
  getReservationDays: Function,
  clearSelections: Function,
  oneWayRoutes?: any,
  oneWayRes?: boolean,
  changeResType: Function,
  changeOneWayRoutes: Function,
  enableOneWay?: boolean,
  changeEnableOneWay: Function,
  searchFiltersSelected?: boolean,
  changeSearchFiltersSelected: Function,
  sendGa4Event: (eventName: string, params?: GA4Params) => void,
  changeTwentyModal: Function,
  loggedIn?: boolean,
  validatedVipVehicles?: string,
  changeMapMarkers: Function,
  changeValidatedVipVehicles: Function,
  changeCustomerBehaviourData: Function,
  gtagTracking?: boolean,
  changeStars: Function,
  changeReservationCreated: Function;
  changeAcrissCodeFromUrl: Function,
  changeModelId: Function,
  changeMapCanInit: Function,
  changeAcrissCodeSet: Function,
  changeShowOneWayReturnLocation: Function,
  changeOpenPopupInCluster: Function,
  pointsInEurosToBeApplied?: number,
  changePointsInEurosToBeApplied: Function,
  couponDescription?: string;
  couponCode?: string;
  changeCouponDescription: Function;
  changeCouponCode: Function;
  storedCoupon?: ICoupon | null;
  changeStoredCoupon: Function;
  onSalesAd?: any;
  changeOnSalesAd: Function;
  sortingTab?: string;
  changeSortingTab: Function;
  showingModelsResults?: boolean;
  changeShowingModelsResults: Function;
  startDateInPast?: boolean;
  changeStartDateInPast: Function;
  openModifyDates: Function,
  changeVirtualPageInfo: Function;
  addSafeDrivePackage: boolean;
  changeAddSafeDrivePackage: Function;
  changeUseCompanyInfo: Function;
  noVatPrices: boolean;
  changeNoVatPrices: Function;
  changeResCancelled: Function;
  sendPaymentGaEvent: Function;
  changeRedirectRef: Function;
}

const defaultState = {
  changeMapMarkers: () => null,
  changeNoUnderTwentyOne: () => null,
  changeMapEnable: () => null,
  changeSearchParameters: () => null,
  changeUser: () => null,
  changeUserLanguage: () => undefined,
  changeVehicle: () => null,
  changeMapData: () => null,
  changeFilteredList: () => null,
  changeFilterParams: () => null,
  changeShowDefaultResults: () => null,
  changeCustomer: () => null,
  changeServices: () => null,
  changeRoute: () => null,
  changeLoggedIn: () => null,
  changeLanguage: () => null,
  clearSelections: () => null,
  getReservationDays: () => null,
  changeResType: () => null,
  changeOneWayRoutes: () => null,
  changeEnableOneWay: () => null,
  changeSearchFiltersSelected: () => null,
  sendGa4Event: () => null,
  changeTwentyModal: () => null,
  changeValidatedVipVehicles: () => null,
  changeCustomerBehaviourData: () => null,
  changeStars: () => null,
  changeAcrissCodeSet: () => null,
  changeShowOneWayReturnLocation: () => null,
  changeOpenPopupInCluster: () => null,
  changePointsInEurosToBeApplied: () => null,
  changeCouponCode: () => null,
  changeCouponDescription: () => null,
  changeStoredCoupon: () => null,
  changeSortingTab: () => null,
  changeShowingModelsResults: () => null,
  sendPaymentGaEvent: () => null,
  changeRedirectRef: () => null,
}

type Props = {
  children?: React.ReactNode
}

export const DataContext = React.createContext<IData>(defaultState as unknown as IData)

const DataProvider = ({ children }: Props) => {
  const rootUrl = ROOT_URL;
  const appContext = APP_CONTEXT;
  const v3ApiUrl = v3_API_URL;
  const walleyApiUrl = walley_API_URL;
  const [route, setRoute] = useState<string>(sessionStorage.getItem("route") || "/");
  const [user, setUser] = useState<User | undefined>(getLocalObject("user"));
  // The user as last set, ahead of the render that shows it: every setUser call below keeps it
  // in step, so changeUserLanguage can merge into the newest user synchronously (row I2).
  const latestUser = useRef<User | undefined>(user);
  const [vehicle, setVehicle] = useState<IVehicleSearch | undefined >(getLocalObject("selectedCar"));
  const [mapData, setMapData] = useState<IVehicleSearch[] | undefined>(getLocalObject("allCars"));
  // C2: a landing whose URL runs a search on its own (?city=, dates, ?carid=…) never shows
  // the sample results first — rendering them and then unmounting them for the startup
  // search was two full-height jumps of everything below the widget on phones. Decided
  // here, before App strips those parameters from the address bar.
  const [showDefaultResults, setShowDefaultResults] = useState(() => !urlTriggersStartupSearch(window.location.search, window.shortcode_parameters?.city));
  const [searchParameters, setSearchParameters] = useState<ISearchParameters | undefined>(getLocalObject("searchParameters"));
  const [noVatPrices, setNoVatPrices] = useState<boolean>(false);
  const [oneWayRoutes, setOneWayRoutes] = useState([]);
  const [enableOneWay, setEnableOneWay] = useState<boolean>(false); // can use this set state when url or WordPress parameters are provided to app
  const [oneWayRes, setOneWayRes] = useState<boolean>(getLocalObject("oneway") ? getLocalObject("oneway") : false);
  const [searchFiltersSelected, setSearchFiltersSelected] = useState(true);
  const [mapEnabled, setMapEnabled] = useState<boolean>(true);
  const [gtagTracking, setGtagTracking] = useState<boolean>(!!(window as any).gtag);
  const [page, setPage] = useState<number>(getLocalObject("page") ?? 0);
  const [validatedVipVehicles, setValidatedVipVehicles] = useState<string>(getLocalObject("vipVehicles") ?? '');
  const [noUnderTwentyOne, setNoUnderTwentyOne] = useState<boolean>(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const [authHashLogin, setAuthHashLogin] = useState(false); // only auth hash login will not see customer info page
  const [filteredVehicles, setFilteredVehicles] = useState<IVehicleSearch[] | undefined>([]);
  const [filteredParams, setFilterParams] = useState<FilterParameters>({});
  const [mapMarkers, setMapMarkers] = useState<IMarker[]>([{} as IMarker]);
  const [stars, setStars] = useState<[] | undefined>(undefined); // vehicle testimonials.json
  const [acrissCodeSet, setAcrissCodeSet] = useState<IAcrissCodes | undefined>(undefined);
  const mobileDevice: boolean = checkDeviceType(); // always start with true, true will work for mobile+desktop, false has disadvantages for mobile
  // services, what's stored, or default value
  const [services, setServices] = useState(getLocalObject("services") ? getLocalObject("services") : serviceDefault);
  const [twentyModal, setTwentyModal] = useState<boolean>(false);
  const [outOfRange, setOutOfRange] = useState(false);
  const [language, setLanguage] = useState('fi');
  const [customerBehaviourUrlData, setCustomerBehaviourUrlData] = useState<ICustomerBehaviourData | undefined>(undefined); // send when searches occur + create res
  const r24Cookie = Check24rCookie();
  const [reservationCreated, setReservationCreated] = useState((getLocalObject("reservationCreated") && getLocalObject("reservationCreated") !== "false") ?? false);
  const [acrissCodeFromUrl, setAcrissCodeFromUrl] = useState<undefined | string>(undefined);
  const [modelId, setModelId] = useState<number[] | undefined>();
  const [mapCanInit, setMapCanInit] = useState(!mobileDevice); // for mobile - this will only be true when they click on "show map" button, desktop is always true
  const [showOneWayReturnLocation, setShowOneWayReturnLocation] = useState< number | undefined >(undefined);
  const [openPopupInCluster, setOpenPopupInCluster] = useState<number | undefined>(undefined);
  const [pointsInEurosToBeApplied, setPointsInEurosToBeApplied] = useState<number>(getLocalObject('pointsInEurosToBeApplied') ?? 0);
  const [storedCoupon, setStoredCoupon] = useState<ICoupon | null>(getLocalObject("coupon") ?? undefined); // replaces redux value: this stores entire object instead of just code
  const [couponCode, setCouponCode] = useState(user?.couponCode! ?? (storedCoupon?.code! ?? ''));
  const [couponDescription, setCouponDescription] = useState(storedCoupon?.description! ?? '');
  const [onSalesAd, setOnSalesAd] = useState<[] | undefined>([]);
  const [sortingTab, setSortingTab] = useState<string>('distance'); // default
  const [showingModelsResults, setShowingModelsResults] = useState<boolean>(false); // false = view all vehicles, true = view models (only for a2a)
  const [startDateInPast, setStartDateInPast] = useState<boolean>(false); // used to show error message if start date is in past
  const [virtualPageInfo, setVirtualPageInfo] = useState<{title: string; path: string} | undefined>(undefined);
  const version = import.meta.env.VITE_VERSION;
  const [addSafeDrivePackage, setAddSafeDrivePackage] = useState<boolean>(false); // for non-ssn customers
  const [useCompanyInfo, setUseCompanyInfo] = useState<boolean>(true);
  const [resCancelled, setResCancelled] = useState<boolean>(false);
  const [redirectRef, setRedirectRef] = useState<RedirectRef | undefined>(
    (sessionStorage.getItem('redirectRef') as RedirectRef) || undefined
  );

  const changeRedirectRef = (ref: RedirectRef) => {
    setRedirectRef(ref);
    sessionStorage.setItem('redirectRef', ref);
  }

  const determinePaymentMethod = (paymentMethodCode: number | undefined) => {
    if (paymentMethodCode === undefined) return undefined;
    // INVOICE (2), QUOTA (3), FREE (6) are internal BE flows — pass through.
    if (paymentMethodCode === paymentMethod.INVOICE
        || paymentMethodCode === paymentMethod.QUOTA
        || paymentMethodCode === paymentMethod.FREE) {
      return paymentMethodCode;
    }
    const isForeigner = !!(customer?.unregisteredForeigner || customer?.registeredForeigner);
    const usePaytrail = import.meta.env.VITE_ENABLE_PAYTRAIL_FOREIGNERS === 'true' && isForeigner;
    if (usePaytrail) {
      if (paymentMethodCode === paymentMethod.STORED_CARD) return 201;
      if (paymentMethodCode === paymentMethod.CREDIT_CARD_INPUT_VAL) {
        return services.saveNewCreditCard
          ? paymentMethod.PAY_AND_STORE_CARD   // 200
          : paymentMethod.CREDIT_CARD_ONCE;    // 1
      }
      return paymentMethod.PAYTRAIL; // 0 — online bank
    }
    return paymentMethod.PAYTRAIL; // 0 — Walley iframe handles all external payments
  }

  useEffect(() => {
    changeEnableOneWay();

    if (searchParameters) {
      if (searchParameters.location) { setShowDefaultResults(false) }
    }

    if ((window as any).gtag && !gtagTracking) { // enable Google Analytics if gtag func exists
      setGtagTracking(true);
    }

    let savedCustomer = getLocalObject("customer") ?? undefined;
    if (savedCustomer) {
      // for reload on payment screen --> saved Customer must be set at start to ensure res data is correct
      setCustomer({...savedCustomer, loggedIn: !!(user?.id && user?.birthDate)});
    }
    // @ts-ignore eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Cross-surface GA4 identity ──────────────────────────────────────────────
  // Two separate problems, both about the launcher webview being invisible in GA4:
  //
  // 1. user-scoped `surface` — the event-scoped stamp added in sendGa4Event lets us
  //    break EVENTS down by surface, but not users or sessions. Setting it as a user
  //    property makes "how many app users vs web users" answerable too.
  //
  // 2. `user_id` — the native shell already reports to the GA4 APP stream with
  //    analytics().setUserId(customer.id) (ReactNativeFE App.tsx). This webview
  //    reports to the WEB stream with its own _ga client id, so today the same
  //    person is counted as two users and their booking session is split in half.
  //    Sending the SAME customer id here lets GA4 stitch them into one user.
  //    Only the authenticated id is used — never a value taken straight off the URL.
  useEffect(() => {
    if (!gtagTracking) return;
    ga4SetUserProperties({ surface: GetSurface() }, { gtagTracking, appContext });
    ga4SetUserId(user?.id ? String(user.id) : null, { gtagTracking, appContext });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gtagTracking, user?.id]);

  useEffect(() => {
    // handles logged in state when tab is reload/refreshed on customer info page
    if(user?.id && user?.birthDate && !reservationCreated) {
      setLoggedIn(true);
    }
  });

  useEffect(() => {
    let wpEmptyDiv = document.getElementById('rrmid');
    if (showDefaultResults && wpEmptyDiv && wpEmptyDiv.style.minHeight !== 'auto') {
      // setting empty loading div height below RRM to auto --> blank space disappears
      wpEmptyDiv.style.minHeight = 'auto';
    }
  }, [showDefaultResults])

  // customer will always be populated from changes in user, services, loggedIn
  const [customer, setCustomer] = useState<any>({
    company: false, // added for our checkbox
    companyName: user?.company?.name ?? '',
    companyVatNumber: user?.company?.vatNumber ?? '',
    companyReference: user?.company?.reference ?? '',
    familyName: user?.familyName ?? '',
    givenName: user?.givenName ?? '',
    unregisteredForeigner: false, //true if user.language === 'en'&& !user.birthDate and customer selects foreigner //&& customer.noSSN
    registeredForeigner: !!user?.birthDate?.length && !(!user?.noPersonalIdCode && user?.personalIdCodeRecorded),
    personalIdCode: user?.personal_id_code ?? '',
    address: user?.address ?? '',
    dateOfBirth: user?.birthDate ?? user?.birthday ?? '',
    noBirthday: true,
    municipality: user?.municipality ?? '',
    postalCode: user?.postalCode ?? '',
    country: user?.country ?? '',
    secret: false,
    prepaid: false,
    phoneNumber: user?.phoneNumber ? user?.phoneNumber.replace(/\s/g,'') : '',
    phoneNumber2: user?.phoneNumber ? user?.phoneNumber.replace(/\s/g,'') : '',
    email: user?.email ? user?.email.trim() : '',
    email2: user?.email ? user?.email.trim() : '',
    additionalDriver: services?.additionalDriver ?? false,
    gdpr: false,
    agree: false,
    agree2: false,
    fairAccept: false,
    emailConsent: '',
    mayUseQuota: !!user?.mayUseQuota, // member
    relativeBankTransferDueDate: user?.relativeBankTransferDueDate,
    mayUseBankTransferAnytime: user?.mayUseBankTransferAnytime,
    loggedIn: loggedIn,
    smsLogin: user?.smsLogin ?? false,
  });

  // reservation will always be populated from changes in user, services, vehicle etc
  const [reservation, setReservation] = useState<any>();

  const handleBusySearching = () => {
    let now = moment();
    let start = moment().hour(8).minute(0);
    let end = moment().hour(21).minute(0);
    if (now.isBetween(end, start) && !customer?.loggedIn) {
      setOutOfRange(true)
    } else {
      setOutOfRange(false)
    }
  }

  useEffect(() => {
    if (user?.saveToCustomer && services) {
      const newCustomer = {
        ...customer,
        companyName: user?.company?.name ?? "",
        companyVatNumber: user?.company?.vatNumber ?? '',
        companyReference: user?.company?.reference ?? '',
        familyName: user?.familyName ?? '',
        givenName: user?.givenName ?? '',
        registeredForeigner: !!user?.birthDate?.length && !(!user?.noPersonalIdCode && user?.personalIdCodeRecorded),
        personalIdCode: user?.personal_id_code ?? '',
        address: user?.address ?? '',
        dateOfBirth: user?.birthDate ?? user?.birthday ?? '',
        municipality: user?.municipality ?? '',
        postalCode: user?.postalCode ?? '',
        country: user?.country ?? '',
        phoneNumber: user?.phoneNumber ?? '',
        phoneNumber2: user?.phoneNumber ?? '',
        email: user?.email ?? '',
        email2: user?.email ?? '',
        additionalDriver: services.additionalDriver,
        agree: user?.tos ?? false,
        mayUseQuota: user?.mayUseQuota, // member
        loggedIn: loggedIn,
      }
      setCustomer(newCustomer);
      sessionStorage.setItem("customer", JSON.stringify(newCustomer));
      const resetUser = {...user, saveToCustomer: false} as User; // reset so customer ris not update on every input change
      latestUser.current = resetUser;
      setUser(resetUser);
    } else {
      if (customer?.loggedIn !== loggedIn) {
        setCustomer({...customer, loggedIn: loggedIn});
        sessionStorage.setItem("customer", JSON.stringify({...customer, loggedIn: loggedIn}));
      }
    }
    // ts-ignore eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, services, loggedIn]);

  useEffect(() => {
    if(route === '/makereservation'){
      const vehicleSession = getLocalObject("selectedCar") ? getLocalObject("selectedCar") : undefined;
      if(vehicleSession ) {
        setNoUnderTwentyOne(vehicleSession.vehicleData?.noundertwentyone ?? false);
      }
    }
  }, [vehicle, route]);

  useEffect(() => {
    if(route === "/" && services !== serviceDefault && !vehicle) {
      // reset services selections if return to search page
      changeServices(serviceDefault);
    }
    if (route === "/") {
      sessionStorage.removeItem('paymentProvider');
      sessionStorage.removeItem('paytrailProviders');
    }
  }, [vehicle, route, services])

  useEffect(() => {
    if (customer && searchParameters && services && vehicle && language) {
      let foreigner = customer?.unregisteredForeigner || customer?.registeredForeigner;
      let insuranceList = services.insurance;
      insuranceList += services.co2compensation ? ',' + services.co2compensation : '';
      insuranceList += services.windshield ? ',1026' : '';
      insuranceList += services.tireInsurance ? ',1038' : '';
      insuranceList += services.cancellationInsurance ? ',1040' : '';
      insuranceList += services.additionalDriver ? ',1041' : '';
      insuranceList += services.safeDrivePackage ? ',1055' : '';
      insuranceList += oneWayRes ? ',1050' : '';
      let reservationProps: any = {
        email: customer?.email ?? user?.phoneNumber ?? '',
        phoneNumber: customer?.phoneNumber ?? user?.phoneNumber ?? 'Number not found',
        agree: !!customer?.agree,
        startDate: moment.isMoment(searchParameters?.startDate) ? searchParameters?.startDate!.clone().format("DD.MM.YYYY")
            : moment(searchParameters?.startDate).format("DD.MM.YYYY"), // if start date is 'null' in some odd case -->
        // it will send that to BE and throw and error rather than add today's date (this will be more of an issue if customer pays for incorrect start date)
        startTime: searchParameters?.startTime,
        endDate: moment.isMoment(searchParameters?.endDate) ? searchParameters?.endDate.clone().format("DD.MM.YYYY")
            : moment(searchParameters?.endDate).format("DD.MM.YYYY"),
        endTime: searchParameters?.endTime,
        km: services.kmRange,
        carId: vehicle?.id,
        insurance: insuranceList,
        export: services.export,
        exportDestination: services.exportDestination.value,
        other: services.hook === true ? 1027 : '',
        quick: 0,
        services: (!services.export && services.kmRange===0)? 'unlimitedKilometers' : '', // for api3
        retry: 0,
        costPool: "",
        discount: vehicle?.onsale?.extra ?
            (!vehicle?.privateData?.vip ?
                isDiscountCodeVip(searchParameters?.discount ?? '') : undefined)
            : (vehicle?.onsale ? undefined : !vehicle?.privateData?.vip ?
                isDiscountCodeVip(searchParameters?.discount ?? '') : searchParameters?.discount ?? undefined),
        ss: user?.smsLogin && user?.noPersonalIdCode !== undefined ? '' : (foreigner ? user?.birthday : customer?.personalIdCode),
        firstname: customer?.givenName ?? user?.givenName ?? '',
        lastname: customer?.familyName ?? user?.familyName ?? '',
        streetAddress: customer?.address,
        zip: customer?.postalCode ? customer?.postalCode.trim() : customer?.postalCode,
        city: customer?.municipality ? customer?.municipality.trim() : customer?.municipality, // trims whitespace on either side of string if it exists
        country: foreigner ? customer?.country : 'Finland',
        noSS:  user?.smsLogin && user?.noPersonalIdCode !== undefined ? user?.noPersonalIdCode : (foreigner), //can be a foreigner with customer?.foreigner===false when birthdate set on backend
        paymentMethod: (determinePaymentMethod(services.paymentMethod)) ?? paymentMethod.PAYTRAIL,
        login: (user?.birthDate && user?.birthDate !== '') && customer?.loggedIn ? 1 : 0, //user is logged in already, let backend know not to look for ssn .
        //Even when customer enters ss, it will be updated on set customer api call not here.
        language,
        onsales: vehicle?.onsale?.extra ? 0 : vehicle?.onsale?.price,
        oneway: oneWayRes,
        additionalDriver: services.additionalDriver ?? false,
        emailConsent: customer?.emailConsent,
        cookie: r24Cookie,
        rrmVersion: version,
        rrmDomain: window.location.host,
        usepoints: pointsInEurosToBeApplied > 0 ? 1 : 0,
        browser: getBrowserName()
      };

      if(oneWayRes) {
        reservationProps.pickupCity = searchParameters?.pickupCity;
        reservationProps.returnCity = searchParameters?.returnCity;
      }

      if (services.additionalDriver) {
        reservationProps.driverNumber = services?.addDriverNumber;
        reservationProps.driverName = services?.addDriverName;
      }

      if (useCompanyInfo && (customer?.companyName || user?.company?.name || services.paymentMethod === paymentMethod.QUOTA)) {
        // customer does not get created if auth hash login, user used in this case
        // use services value retrieved from customer if quota available but not company data (does not save to customer profile for this case)
        reservationProps.companyName = services.quotaCompanyName ?? customer?.companyName ?? user?.company?.name ;
        reservationProps.companyVatNumber = services.quotaCompanyId ?? customer?.companyVatNumber ?? user?.company?.vatNumber;
        reservationProps.companyReference = customer?.companyReference ?? user?.company?.reference;
      }

      if(customerBehaviourUrlData) {
        reservationProps.utm_campaign = getCookie('lumo') ? 'lumo' : customerBehaviourUrlData.utm_campaign ?? ''; // check for lumo cookie first
        reservationProps.utm_source = customerBehaviourUrlData.utm_source ?? '';
        reservationProps.utm_term = customerBehaviourUrlData.utm_term ?? '';
        reservationProps.utm_medium = customerBehaviourUrlData.utm_medium ?? '';
        reservationProps.utm_content = customerBehaviourUrlData.utm_content ?? '';
        reservationProps.gclid = customerBehaviourUrlData.gclid ?? '';
        reservationProps.cid = user?.id ?? customerBehaviourUrlData.cid;
      } else {
        // if no customer behaviour data, check for lumo cookie anyway
        reservationProps.utm_campaign = getCookie('lumo') ? 'lumo' : '';
      }
      setReservation(reservationProps);
    }

  }, [customer, searchParameters, services, vehicle, language]);

  const changeSearchParameters = (searchParams: ISearchParameters) => {
    // check if provided date times are in correct intervals
    let {newStartDate, newEndDate} = validateStartEndValues(searchParams?.startDate, searchParams?.endDate);
    let newSearchParameters = searchParams ? {...searchParams, mobileDevice: mobileDevice,
      startDate: newStartDate ?? searchParams.startDate,
      startTime: newStartDate ? moment(newStartDate).format('HH:mm') : searchParams.startTime,
      endDate: newEndDate ?? searchParams.endDate,
      endTime: newEndDate ? moment(newEndDate).format('HH:mm') : searchParams.endTime } : searchParams;
    sessionStorage.setItem("searchParameters", JSON.stringify(newSearchParameters));
    setSearchParameters(newSearchParameters);
    // Mirror the current segment as a user-scoped GA4 property for user-level funnel breakdowns.
    const segment = segmentFromSearchParams(newSearchParameters);
    if (segment) {
      ga4SetUserProperties({ vehicle_segment: segment }, { gtagTracking, appContext });
    }
    // Capture the ORIGINAL intent once per session — never overwritten by later toggles/
    // filters. This is the "what did they come in searching for" (car vs van) metric.
    //
    // It must key off the EXPRESSED segment, not segmentFromSearchParams(): that one falls
    // back to 'car' whenever nothing was expressed, and this function runs on every search
    // parameter change (date edits, city clears, URL parsing) — all of which happen before
    // the customer ever presses a segment button. Keying off the fallback locked every
    // session to 'car' on its first parameter change, so `first_segment` recorded 'van'
    // zero times in production while `vehicle_segment` recorded hundreds of van searches.
    const expressedSegment = expressedSegmentFromSearchParams(newSearchParameters);
    if (expressedSegment && !sessionStorage.getItem('first_segment')) {
      sessionStorage.setItem('first_segment', expressedSegment);
      ga4SetUserProperties({ first_segment: expressedSegment }, { gtagTracking, appContext });
    }
  }

  const changeFilteredList = (filteredVehicles: IVehicleSearch[] | undefined) => {
    if(filteredVehicles && filteredVehicles.length > 0){
      setFilteredVehicles(filteredVehicles);
    } else {
        setFilteredVehicles([]);
    }
  }

  const changeFilterParams = (filterParams: FilterParameters) => {
    setFilterParams(filterParams);
  }

  const changeMapMarkers = (mapMarkers: IMarker[]) => {
    setMapMarkers(mapMarkers);
  }

  const changeTwentyModal = (twentyModal: boolean) => {
    setTwentyModal(twentyModal);
  }

  const changeUser = (user: User) => {
    sessionStorage.setItem("user", JSON.stringify(user));
    latestUser.current = user;
    setUser(user);
  }

  // Merges into the newest user — not the one a caller's render saw (English can register after
  // a hash login has replaced it), nor sessionStorage's (the customer-init effect resets
  // saveToCustomer in state only). Synchronous, so a later clearSelections in the same batch
  // still wins (row I2).
  const changeUserLanguage = (language: string) => {
    changeUser({...latestUser.current, language} as User);
  }

  const changeMapEnable = (mapEnabled: boolean) => {
    setMapEnabled(mapEnabled);
    sessionStorage.setItem("mapEnabled", JSON.stringify(mapEnabled));
  }

  const changeMapData = (data: IVehicleSearch[]) => {
    // State first: the "allCars" copy is only a rehydration hint for a page reload (the map reads
    // dataContext.mapData first and falls back to storage, see Map/googleMaps.tsx), so a failed
    // cache write must never stop the results from rendering.
    setMapData(data);
    cacheMapData(data);
  }

  const changeShowDefaultResults = (defaultResults: boolean) => {
    setShowDefaultResults(defaultResults);
  }

  const changeVehicle = (vehicle: IVehicleSearch | null) => {
    if (vehicle === null) {
      sessionStorage.removeItem("selectedCar");
      setVehicle(undefined);
    } else {
      sessionStorage.setItem("selectedCar", JSON.stringify(vehicle));
      setVehicle(vehicle);
    }

  }

  const changeCustomer = (customer: Customer) => {
    sessionStorage.setItem("customer", JSON.stringify(customer));
    setCustomer(customer);
  }

  const changeServices = (services: any) => {
    sessionStorage.setItem("services", JSON.stringify(services));
    setServices(services);
  }

  const changeLoggedIn = (loggedIn: boolean, authHash?: string) => {
    if(authHash && authHash !== '' && authHash !== 'remove' && loggedIn){
        setLoggedIn(true);
        setAuthHashLogin(true);
    } else {
      if (authHash === 'remove') {
        // explicitly setting auth hash to false, reset to view customer info page
        setAuthHashLogin(false);
        sessionStorage.removeItem('AH-L');
      }
      if (!loggedIn && !useCompanyInfo) {
        setUseCompanyInfo(true); // always default if logged out/can see customer info page
      }
      setLoggedIn(loggedIn);
    }
  }

  const changeRoute = (route: string) => {
    if (route === 'null') {
        route = '/';
    }
    setRoute(route)
    sessionStorage.setItem("route", route)
  }

  const changeLanguage = (language: string) => {
    setLanguage(language);
  }

  const changePage = (page: number) => {
    const storedPage = sessionStorage.getItem('page');
    const newPage = page ? page : (storedPage ? JSON.parse(storedPage) : 0);
    sessionStorage.setItem("page", JSON.stringify(newPage));
    setPage(newPage);
  }

  const getReservationDays = () => {
    // Used to get multiplier to calculate prices
    // 24 hours = 1
    // 1 day 1 hours 0 mins = 2, and so on...
    if (!searchParameters) {
      return 0;
    }
    let startD = moment(searchParameters.startDate)
    let endD = moment(searchParameters.endDate);
    let minutesAfterFullDays = (endD.diff(startD, "minutes"))%(24*60);
    if (minutesAfterFullDays === 0) {
      return (endD.diff(startD, "days"));
    } else {
      return (endD.diff(startD, "days") + 1);
    }
  }

  const sendGa4Event = (eventName: string, params: GA4Params = {},) => {
    const segment = segmentFromSearchParams(searchParameters);
    const defaults: GA4Params = { surface: GetSurface() };
    if (segment) defaults.vehicle_segment = segment;
    ga4Event(eventName, { ...defaults, ...params }, { gtagTracking: gtagTracking, appContext: appContext});
  };

  const buildCustomerFromUser = (currentUser: User | undefined, currentLoggedIn: boolean) => ({
    company: false,
    companyName: currentUser?.company?.name ?? '',
    companyVatNumber: currentUser?.company?.vatNumber ?? '',
    companyReference: currentUser?.company?.reference ?? '',
    familyName: currentUser?.familyName ?? '',
    givenName: currentUser?.givenName ?? '',
    unregisteredForeigner: false,
    registeredForeigner: !!currentUser?.birthDate?.length && !(!currentUser?.noPersonalIdCode && currentUser?.personalIdCodeRecorded),
    personalIdCode: currentUser?.personal_id_code ?? '',
    address: currentUser?.address ?? '',
    dateOfBirth: currentUser?.birthDate ?? currentUser?.birthday ?? '',
    noBirthday: true,
    municipality: currentUser?.municipality ?? '',
    postalCode: currentUser?.postalCode ?? '',
    country: currentUser?.country ?? '',
    secret: false,
    prepaid: false,
    phoneNumber: currentUser?.phoneNumber ? currentUser?.phoneNumber.replace(/\s/g,'') : '',
    phoneNumber2: currentUser?.phoneNumber ? currentUser?.phoneNumber.replace(/\s/g,'') : '',
    email: currentUser?.email ? currentUser?.email.trim() : '',
    email2: currentUser?.email ? currentUser?.email.trim() : '',
    additionalDriver: false,
    gdpr: false,
    agree: false,
    agree2: false,
    fairAccept: false,
    emailConsent: '',
    mayUseQuota: !!currentUser?.mayUseQuota,
    relativeBankTransferDueDate: currentUser?.relativeBankTransferDueDate,
    mayUseBankTransferAnytime: currentUser?.mayUseBankTransferAnytime,
    loggedIn: currentLoggedIn,
    smsLogin: currentUser?.smsLogin ?? false,
  });

  const clearSelections = () => {
    sessionStorage.removeItem("customer");
    sessionStorage.removeItem("user");
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("selectedCar");
    sessionStorage.removeItem("services");
    sessionStorage.removeItem("searchParameters");
    sessionStorage.removeItem("oneway");
    sessionStorage.removeItem("allCars");
    sessionStorage.removeItem("vipVehicles");
    sessionStorage.removeItem("coupon");
    sessionStorage.removeItem("urlCodeValid");
    sessionStorage.removeItem("paymentProviders");
    sessionStorage.removeItem("paytrailProviders");
    sessionStorage.removeItem("paymentProvider");
    sessionStorage.removeItem("paymentPending");
    sessionStorage.removeItem("paytrailReselection");
    sessionStorage.removeItem("rid-1");
    // The Walley checkout token belongs to the booking above (kept across a reload of the thank-you page).
    clearWalleyPaymentToken();
    sessionStorage.removeItem('pointsInEurosToBeApplied');
    sessionStorage.removeItem("autoAppliedCoupon");
    sessionStorage.removeItem('AT-L');
    sessionStorage.removeItem('paymentSuccess');
    sessionStorage.removeItem(AUTO_CUSTOMER_INFO_KEY);
    clearPendingPurchase();
    setCustomer(undefined);
    latestUser.current = undefined;
    setUser(undefined);
    setLoggedIn(false);
    setVehicle(undefined);
    setServices(serviceDefault);
    setSearchParameters(undefined);
    setMapData([]);
    setTwentyModal(false)
    setValidatedVipVehicles('');
    setReservationCreated(false);
    setAuthHashLogin(false);
  }

  const changeResType = (type: boolean) => {
    // changes between OneWay and a2a res options
    setOneWayRes(type);
    sessionStorage.setItem("oneway", JSON.stringify(type));
    changeShowingModelsResults(false);
    changeModelId(undefined);
  }

  const changeOneWayRoutes = (routes: any) => {
    setOneWayRoutes(routes);
  }

  const changeEnableOneWay = () => {
    if(import.meta.env.VITE_ENABLE_ONE_WAY_FEATURE){
      setEnableOneWay(import.meta.env.VITE_ENABLE_ONE_WAY_FEATURE === 'true');
    } else setEnableOneWay(false); // if no initial env. variable found, disable one way feature
  }

  const changeSearchFiltersSelected = (filtersSelected: boolean) => {
    setSearchFiltersSelected(filtersSelected);
  }

  const changeValidatedVipVehicles = (vehicleId: number) => {
    if (vehicleId !== -1) {
      let vipVehiclesArr = validatedVipVehicles;
      vipVehiclesArr += "," + vehicleId.toString();
      sessionStorage.setItem('vipVehicles', JSON.stringify(vipVehiclesArr));
      setValidatedVipVehicles(vipVehiclesArr.toString());
    } else if (validatedVipVehicles.length) {
      // clear vip vehicles if new coupon applied
      sessionStorage.removeItem('vipVehicles');
      setValidatedVipVehicles('');
    }
  }

  const changeCustomerBehaviourData = (behaviourData: ICustomerBehaviourData) => {
    setCustomerBehaviourUrlData(behaviourData);
  }

  const changeStars = (starsData: any) => {
    if(typeof starsData === "object") {
      setStars(starsData);
    }
  }

  const changeReservationCreated = (resCreated: any) => {
    setReservationCreated(resCreated);
  }

  const changeAcrissCodeFromUrl = (acrissCode: string | undefined) => {
    let acrissCodeToString;
    if (acrissCode){
      switch(acrissCode) {
        case '1':
          acrissCodeToString = 'compact';
          break;
        case '2':
          acrissCodeToString = 'stw';
          break;
        case '3':
          acrissCodeToString = 'suv';
          break;
        case '4':
          acrissCodeToString = 'minibus';
          break;
        case '5':
          acrissCodeToString = 'van';
          break;
        default:
          return;
      }
    }

    if (acrissCodeToString) {
      setAcrissCodeFromUrl(acrissCodeToString);
    } else {
      setAcrissCodeFromUrl(undefined);
    }
  }

  const changeModelId = (modelIdNum: number[] | undefined) => {
    setModelId(modelIdNum);
  }

  const changeMapCanInit = () => {
    setMapCanInit(true);
  }

  const changeOnSalesAd = (onSalesAdData: [] | undefined) => {
    if (!onSalesAdData) {
      setOnSalesAd(undefined); // if undefined, do not try to set it again (retrieve once in session)
      return;
    }
    setOnSalesAd(onSalesAdData);
  }

  const changeAcrissCodeSet = (acrissCodesData: IAcrissCodes | undefined) => {
    if (!acrissCodesData) {
      return;
    }

    if (acrissCodesData.stw) { // check one of the expected objects and if present, set all data
      setAcrissCodeSet(acrissCodesData);
    }
  }

  const changeShowOneWayReturnLocation = (oneWayVehicleData: number | undefined) => {
    if (!oneWayVehicleData && showOneWayReturnLocation) {
      setShowOneWayReturnLocation(undefined);
    } else {
      setShowOneWayReturnLocation(oneWayVehicleData);
    }
  }

  const changeOpenPopupInCluster = (openId: number | undefined) => {
    setOpenPopupInCluster(openId);
  }

  const changePointsInEurosToBeApplied = (points: number) => {
    sessionStorage.setItem('pointsInEurosToBeApplied', JSON.stringify(points));
    setPointsInEurosToBeApplied(points);
  }

  const changeCouponDescription = (description: string) => {
    setCouponDescription(description);
  }

  const changeCouponCode = (code: string) => {
    // this is coupon code for access through RRM,
    // local code for input field change can be found within Coupon component (these should match though)
    // normalised here as well as at entry so codes restored from an older session are standardised too
    setCouponCode(normalizeCouponCode(code));
  }

  const changeStoredCoupon = (coupon: ICoupon | null) => {
    // this coupon object is used to ensure state is kept even after user refreshes tab
    // code is normalised because GA4 purchase events report storedCoupon.code
    const normalized = coupon ? { ...coupon, code: normalizeCouponCode(coupon.code) } : null;
    setStoredCoupon(normalized);
    if (normalized) {
      sessionStorage.setItem('coupon', JSON.stringify(normalized));
    } else sessionStorage.removeItem('coupon');
  }

  const changeSortingTab = (tab: string) => {
    setSortingTab(tab);
  }

  const changeShowingModelsResults = (modelResults: boolean) => {
    setShowingModelsResults(modelResults);
  }

  const changeStartDateInPast = (startDateInPast: boolean) => {
    setStartDateInPast(startDateInPast);
  }

  const openModifyDates = () => {
    // force dates changes in startdate is in past
    const element = document.getElementById('r24-modify-dates');
    element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (element && typeof element.click === 'function') {
      element.click();
    }
  }

  const changeVirtualPageInfo = (pageInfo: string | undefined) => {
    if (!pageInfo) {
      // reset before getting new results
      setVirtualPageInfo(undefined);
      return;
    }
    let pathTitle = virtualPageViews[pageInfo]
    if (!virtualPageInfo || pathTitle.path !== virtualPageInfo.path || pathTitle.title !== virtualPageInfo.title) {
      setVirtualPageInfo(pathTitle);
      sendVirtualPageView(pathTitle.path, pathTitle.title, {gtagTracking: gtagTracking, appContext: appContext,
        params: { surface: GetSurface() }});
    }
  }

  const changeAddSafeDrivePackage = (addPackage: boolean) => {
    if (!vehicle?.pricingData?.base?.safeDrivePackage) {
      // if no safe drive package, ignore feature
      return;
    }
    if (reservationCreated) {
      // if res created, user viewed as logged out --> services determine if safe drive was originally added instead
      if (services?.addSafeDrivePackage === true) {
        setAddSafeDrivePackage(true);
      }
      return;
    }
    setAddSafeDrivePackage(addPackage);
  }

  const changeNoVatPrices= () => {
    setNoVatPrices(!noVatPrices);
    // add session storage here if needed later
  }

  const changeUseCompanyInfo = (useCompany: boolean) => {
    setUseCompanyInfo(useCompany);
  }

  const changeResCancelled = (resCancelled: boolean) => {
    setResCancelled(resCancelled);
  }

  const sendPaymentGaEvent = (price: number, resId: number) => {
    if (!reservation || !vehicle) {
      return;
    }
    // Build the purchase payload now (all reservation data is in hand), but only
    // *fire* it when payment is actually complete:
    //   - Internal flows (invoice/quota/free) are paid at create → fire now.
    //   - Walley iframe + stored-card flows complete later → stash the payload and
    //     fire it from the success point (App.checkForWalleyCallback / stored-card
    //     charge success), so an abandoned payment never records a purchase.
    const purchasePayload = buildPurchasePayload(
        gtagTracking ?? false,
        noVatPrices,
        reservation,
        vehicle,
        getReservationDays(),
        price, resId,
        storedCoupon?.code ?? undefined,
        paymentTypeLabel(services?.paymentMethod));

    if (purchasePayload) {
      const method = Number(reservation.paymentMethod);
      const paidAtCreate = method === paymentMethod.INVOICE
                        || method === paymentMethod.QUOTA
                        || method === paymentMethod.FREE;
      if (paidAtCreate) {
        firePurchaseEvent(purchasePayload, sendGa4Event);
      } else {
        storePendingPurchase(purchasePayload);
      }
    }

    // if one-way or onSales res: GA4 event is sent accordingly,
    // else, normal res: Payment method is checked to determine is Normal, Free or Quota res GA4 event
    if (oneWayRes) {
      sendGa4Event("create_reservation", {
        step: "confirm",
        reservation_result: "success",
        rental_origin: "one_way"
      });
    } else if(vehicle?.onsale?.extra) {
      sendGa4Event("create_reservation", {
        step: "confirm",
        reservation_result: "success",
        rental_origin: "extra_on_sales"
      });
    } else if (vehicle?.onsale) {
      sendGa4Event("create_reservation", {
        step: "confirm",
        reservation_result: "success",
        rental_origin: "on_sales"
      });
    } else {
      if(reservation.paymentMethod === 6) {
        sendGa4Event("create_reservation", {
          step: "confirm",
          reservation_result: "success",
          rental_origin: "free"
        });
      } else if (reservation.paymentMethod === 3) {
        sendGa4Event("create_reservation", {
          step: "confirm",
          reservation_result: "success",
          rental_origin: "quota"
        });
      } else {
        sendGa4Event("create_reservation", {
          step: "confirm",
          reservation_result: "success",
          rental_origin: "a2a"
        });
      }
    }
  }

  const state = {
    rootUrl,
    appContext,
    v3ApiUrl,
    walleyApiUrl,
    noUnderTwentyOne,
    customer,
    mapMarkers,
    filteredParams,
    searchParameters,
    user,
    vehicle,
    route,
    language,
    mapData,
    services,
    reservation,
    mapEnabled,
    twentyModal,
    oneWayRoutes,
    oneWayRes,
    enableOneWay,
    searchFiltersSelected,
    outOfRange,
    page,
    loggedIn,
    validatedVipVehicles,
    gtagTracking,
    filteredVehicles,
    stars,
    mobileDevice,
    reservationCreated,
    acrissCodeFromUrl,
    modelId,
    mapCanInit,
    authHashLogin,
    acrissCodeSet,
    showOneWayReturnLocation,
    openPopupInCluster,
    pointsInEurosToBeApplied,
    couponDescription,
    couponCode,
    storedCoupon,
    onSalesAd,
    sortingTab,
    showingModelsResults,
    startDateInPast,
    addSafeDrivePackage,
    noVatPrices,
    resCancelled,
    redirectRef,
    changeMapMarkers,
    changeFilterParams,
    changeFilteredList,
    handleBusySearching,
    changeNoUnderTwentyOne: setNoUnderTwentyOne,
    changeSearchParameters,
    changeUser,
    changeUserLanguage,
    changeVehicle,
    changeMapData,
    changeShowDefaultResults,
    changeCustomer,
    changeServices,
    changeLoggedIn,
    changeRoute,
    changeLanguage,
    getReservationDays,
    clearSelections,
    changeTwentyModal,
    changeResType,
    changeOneWayRoutes,
    changeEnableOneWay,
    changeSearchFiltersSelected,
    changeMapEnable,
    sendGa4Event,
    showDefaultResults,
    useCompanyInfo,
    changePage,
    changeValidatedVipVehicles,
    changeCustomerBehaviourData,
    changeStars,
    changeReservationCreated,
    changeAcrissCodeFromUrl,
    changeModelId,
    changeMapCanInit,
    changeAcrissCodeSet,
    changeShowOneWayReturnLocation,
    changeOpenPopupInCluster,
    changePointsInEurosToBeApplied,
    changeCouponDescription,
    changeCouponCode,
    changeStoredCoupon,
    changeOnSalesAd,
    changeSortingTab,
    changeShowingModelsResults,
    changeStartDateInPast,
    openModifyDates,
    changeVirtualPageInfo,
    changeAddSafeDrivePackage,
    changeUseCompanyInfo,
    changeNoVatPrices,
    changeResCancelled,
    sendPaymentGaEvent,
    changeRedirectRef
  }

  return <DataContext.Provider value={state}>{children}</DataContext.Provider>
}

export default DataProvider
